import { selector } from "recoil"

import { getActiveLoanForProvider } from "~/containers/Proposal/Brochure/FinancialSummarySlide/credit-application-modal/utils"
import type { LoanProduct } from "~/graphql/client"
import { FinanceType } from "~/utils/finance/constants"
import { ensureAllFieldsDefined } from "~/utils/finance/defined"

import {
  proposalLocalState,
  proposalRemoteState,
} from "../proposal/proposal-state"
import { siteRemoteState } from "../site/site-state"

export const checkIfLoanProductIsTpo = (
  loanProduct: LoanProduct | null | undefined,
): boolean => loanProduct?.providerMeta?.type === `PPO`

export const isTpoProductSelectedState = selector<boolean>({
  key: `isTpoProductSelected`,
  get: ({ get }) => {
    const proposal = get(proposalLocalState)

    return (
      checkIfLoanProductIsTpo(proposal.selectedLoanProduct) ||
      proposal.financeType === FinanceType.LIGHT_REACH
    )
  },
})

type TpoActiveLoanProviderMetadata = {
  offerId: string
}

export const tpoActiveLoanProviderMetadataState =
  selector<TpoActiveLoanProviderMetadata | null>({
    key: `tpoActiveLoanProviderMetadata`,
    get: ({ get }) => {
      const isTpoProductSelected = get(isTpoProductSelectedState)
      const proposal = get(proposalRemoteState)

      if (!isTpoProductSelected) {
        return null
      }

      const selectedProvider = proposal.selectedLoanProduct?.provider
      const activeLoan = getActiveLoanForProvider(
        proposal.activeLoans ?? [],
        selectedProvider,
      )

      return ensureAllFieldsDefined<TpoActiveLoanProviderMetadata>({
        offerId: activeLoan?.providerMetadata?.offerId,
      })
    },
  })

export const tpoSellerLicenseErrorState = selector<boolean>({
  key: `tpoSellerLicenseError`,
  get: ({ get }) => {
    const isTpoProductSelected = get(isTpoProductSelectedState)
    const site = get(siteRemoteState)
    const { seller, mentor, primaryPresenter } = get(proposalRemoteState)

    // If TPO product is not selected, return false immediately
    if (!isTpoProductSelected) {
      return false
    }

    // Define states that require licenses
    const statesRequiringLicense = [`CA`, `CT`, `MD`]
    const licenseRequired = statesRequiringLicense.includes(site.state)

    // If no license is required for this state, return false
    if (!licenseRequired) {
      return false
    }

    const entityToCheck =
      mentor?.powurUserId === primaryPresenter ? mentor : seller

    // Handle cases where licenses is null or empty array
    if (!entityToCheck.licenses?.length) {
      return true
    }

    // Check if seller has a license for the current state
    const hasLicenseForState = entityToCheck.licenses.some(
      (license) => license.state === site.state,
    )

    // Return true if seller doesn't have a license for this state
    return !hasLicenseForState
  },
})
